// ===================== Camera =====================
import { lerp } from './math.js';
import { Settings } from './settings.js';

export class Camera {
  constructor() {
    this.x = 0; this.y = 0;      // world position at screen center
    this.zoom = 1;
    this.w = 1; this.h = 1;      // viewport size (css px)
    this.shakeT = 0; this.shakeMag = 0;
    this._sx = 0; this._sy = 0;  // current shake offset
  }

  resize(w, h) {
    this.w = w; this.h = h;
    // Slight zoom-out on very large screens so the play area feels consistent.
    this.zoom = Math.max(0.85, Math.min(1.25, Math.min(w, h) / 820));
  }

  follow(tx, ty, dt) {
    // Smooth follow.
    const k = 1 - Math.pow(0.0001, dt);
    this.x = lerp(this.x, tx, k);
    this.y = lerp(this.y, ty, k);
  }

  snap(tx, ty) { this.x = tx; this.y = ty; }

  shake(mag, time = 0.3) {
    if (!Settings.shake) return;
    this.shakeMag = Math.max(this.shakeMag, mag);
    this.shakeT = Math.max(this.shakeT, time);
  }

  update(dt) {
    if (this.shakeT > 0) {
      this.shakeT -= dt;
      const m = this.shakeMag * (this.shakeT > 0 ? 1 : 0);
      // Presentation-only randomness — deliberately NOT the sim RNG. shake()
      // early-returns when Settings.shake is off, so drawing from the seeded
      // stream here would let a user toggle change the simulation.
      this._sx = (Math.random() * 2 - 1) * m;
      this._sy = (Math.random() * 2 - 1) * m;
      if (this.shakeT <= 0) { this._sx = 0; this._sy = 0; this.shakeMag = 0; }
    } else { this._sx = 0; this._sy = 0; }
  }

  // Scalar transforms for hot render loops — no per-call object. The transform
  // is axis-separable, so each takes only the coordinate it needs.
  worldToScreenX(wx) { return (wx - this.x) * this.zoom + this.w / 2 + this._sx; }
  worldToScreenY(wy) { return (wy - this.y) * this.zoom + this.h / 2 + this._sy; }

  // Allocating variant — fine for cold call sites; hot loops use the scalars.
  worldToScreen(wx, wy) {
    return {
      x: this.worldToScreenX(wx),
      y: this.worldToScreenY(wy),
    };
  }
  screenToWorld(sx, sy) {
    return {
      x: (sx - this.w / 2 - this._sx) / this.zoom + this.x,
      y: (sy - this.h / 2 - this._sy) / this.zoom + this.y,
    };
  }
  // Is a world circle visible (with margin)? Called per entity per frame —
  // scalar transforms keep it allocation-free.
  visible(wx, wy, r) {
    const sx = this.worldToScreenX(wx), sy = this.worldToScreenY(wy);
    const m = r * this.zoom + 40;
    return sx > -m && sx < this.w + m && sy > -m && sy < this.h + m;
  }
}
